/**
 * Everything Nubian — lightweight progressive enhancement
 * Header drawer, featured gallery, newsletter prototype, scroll reveal, hero slider
 */
(function () {
  'use strict';

  document.documentElement.classList.add('js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Mobile navigation ---------- */
  function initNav() {
    var toggle = document.querySelector('[data-en-nav-toggle]');
    var drawer = document.querySelector('[data-en-nav-drawer]');
    var closeBtn = document.querySelector('[data-en-nav-close]');
    if (!toggle || !drawer) return;

    var lastFocus = null;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      drawer.hidden = !open;
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        lastFocus = document.activeElement;
        var focusable = drawer.querySelector('a, button');
        if (focusable) focusable.focus();
      } else if (lastFocus) {
        lastFocus.focus();
      }
    }

    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        setOpen(false);
      });
    }

    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) setOpen(false);
      if (e.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
      }
    });
  }

  /* ---------- Featured product gallery ---------- */
  function initGalleries() {
    document.querySelectorAll('[data-en-gallery]').forEach(function (gallery) {
      var main = gallery.querySelector('[data-en-gallery-main]');
      var thumbs = gallery.querySelectorAll('[data-en-gallery-thumb]');
      if (!main || !thumbs.length) return;

      function select(thumb) {
        var src = thumb.getAttribute('data-full-src');
        var alt = thumb.getAttribute('data-alt') || '';
        if (!src) return;

        main.classList.add('is-fading');
        window.setTimeout(
          function () {
            main.src = src;
            main.alt = alt;
            main.classList.remove('is-fading');
          },
          reduceMotion ? 0 : 160
        );

        thumbs.forEach(function (t) {
          var selected = t === thumb;
          t.setAttribute('aria-current', selected ? 'true' : 'false');
          t.classList.toggle('is-active', selected);
        });
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          select(thumb);
        });
        thumb.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            select(thumb);
          }
        });
      });
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    if (reduceMotion) {
      document.querySelectorAll('.en-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.en-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.en-reveal').forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------- Homepage hero Product Worlds slider ---------- */
  function initHeroSlider() {
    document.querySelectorAll('[data-en-hero-slider]').forEach(function (root) {
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-en-hero-slide]'));
      if (slides.length < 2) return;

      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-en-hero-dot]'));
      var prevBtn = root.querySelector('[data-en-hero-prev]');
      var nextBtn = root.querySelector('[data-en-hero-next]');
      var currentEl = root.querySelector('[data-en-hero-current]');
      var autoplayEnabled = root.getAttribute('data-autoplay') === 'true' && !reduceMotion;
      var autoplayMs = parseInt(root.getAttribute('data-autoplay-ms'), 10) || 7000;
      var index = 0;
      var timer = null;
      var userPaused = false;
      var offscreen = false;
      var touchStartX = 0;
      var touchDeltaX = 0;

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function hydrateSlideMedia(slide) {
        if (!slide || slide.getAttribute('data-en-hero-hydrated') === 'true') return;
        slide.querySelectorAll('[data-en-hero-lazy-src]').forEach(function (img) {
          var src = img.getAttribute('data-en-hero-lazy-src');
          if (!src) return;
          img.setAttribute('src', src);
          img.removeAttribute('data-en-hero-lazy-src');
        });
        slide.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          if (img.getAttribute('src')) img.setAttribute('loading', 'eager');
        });
        slide.setAttribute('data-en-hero-hydrated', 'true');
      }

      function goTo(nextIndex) {
        var total = slides.length;
        index = ((nextIndex % total) + total) % total;

        slides.forEach(function (slide, i) {
          var active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (active) {
            slide.removeAttribute('inert');
            hydrateSlideMedia(slide);
          } else {
            slide.setAttribute('inert', '');
          }

          var focusables = slide.querySelectorAll('a, button');
          focusables.forEach(function (el) {
            if (active) el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', '-1');
          });
        });

        dots.forEach(function (dot, i) {
          var active = i === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        if (currentEl) currentEl.textContent = pad(index + 1);
        syncAutoplay();
      }

      function next() {
        goTo(index + 1);
      }

      function prev() {
        goTo(index - 1);
      }

      function stopAutoplay() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      function syncAutoplay() {
        stopAutoplay();
        if (!autoplayEnabled || userPaused || offscreen) return;
        timer = window.setInterval(next, autoplayMs);
      }

      function setUserPaused(value) {
        userPaused = value;
        syncAutoplay();
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          prev();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          next();
        });
      }

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var i = parseInt(dot.getAttribute('data-slide-index'), 10);
          if (!isNaN(i)) goTo(i);
        });
      });

      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          next();
        }
      });

      root.addEventListener('mouseenter', function () {
        setUserPaused(true);
      });
      root.addEventListener('mouseleave', function () {
        setUserPaused(false);
      });
      root.addEventListener('focusin', function () {
        setUserPaused(true);
      });
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) setUserPaused(false);
      });

      root.addEventListener(
        'touchstart',
        function (e) {
          if (!e.changedTouches || !e.changedTouches.length) return;
          touchStartX = e.changedTouches[0].clientX;
          touchDeltaX = 0;
          setUserPaused(true);
        },
        { passive: true }
      );

      root.addEventListener(
        'touchmove',
        function (e) {
          if (!e.changedTouches || !e.changedTouches.length) return;
          touchDeltaX = e.changedTouches[0].clientX - touchStartX;
        },
        { passive: true }
      );

      root.addEventListener(
        'touchend',
        function () {
          if (Math.abs(touchDeltaX) > 48) {
            if (touchDeltaX < 0) next();
            else prev();
          }
          setUserPaused(false);
          touchDeltaX = 0;
        },
        { passive: true }
      );

      if (!reduceMotion) {
        var parallaxRaf = 0;
        var pendingParallax = null;
        root.addEventListener(
          'pointermove',
          function (e) {
            if (window.matchMedia('(max-width: 820px)').matches) return;
            pendingParallax = e;
            if (parallaxRaf) return;
            parallaxRaf = window.requestAnimationFrame(function () {
              parallaxRaf = 0;
              var ev = pendingParallax;
              pendingParallax = null;
              if (!ev) return;
              var rect = root.getBoundingClientRect();
              var x = (ev.clientX - rect.left) / rect.width - 0.5;
              var y = (ev.clientY - rect.top) / rect.height - 0.5;
              var activeSlide = slides[index];
              if (!activeSlide) return;
              var targets = activeSlide.querySelectorAll('.en-hero__image, [data-en-hero-parallax]');
              targets.forEach(function (el) {
                el.style.transform =
                  'translate3d(' + (x * -8).toFixed(2) + 'px, ' + (y * -5).toFixed(2) + 'px, 0) scale(1.02)';
              });
            });
          },
          { passive: true }
        );
        root.addEventListener('pointerleave', function () {
          slides.forEach(function (slide) {
            slide.querySelectorAll('.en-hero__image, [data-en-hero-parallax]').forEach(function (el) {
              el.style.transform = '';
            });
          });
        });
      }

      if ('IntersectionObserver' in window) {
        var heroIo = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              offscreen = !entry.isIntersecting;
              syncAutoplay();
            });
          },
          { threshold: 0.2 }
        );
        heroIo.observe(root);
      }

      goTo(0);
      syncAutoplay();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initNav();
    initGalleries();
    initReveal();
    initHeroSlider();
    });
  } else {
    initNav();
    initGalleries();
    initReveal();
    initHeroSlider();
  }
})();
